// A cache of FX series per currency pair, for backtests in another currency.
// Past fixings never change, so a fetched range is reused for any request inside
// it; a range reaching the last week is refetched after a few minutes (today's
// fixing may have been published since). Plain TS with the fetch injected, so it
// runs and is tested without the network.

/** One pair's daily rates, oldest first (as numbers: the backtest report is analysis). */
export interface FxFixingSeries {
  readonly from: string
  readonly to: string
  readonly source: string
  readonly derivedVia: string
  readonly dates: ReadonlyArray<string>
  readonly rates: ReadonlyArray<number>
}

export type FetchFxSeries = (from: string, to: string, start: string, end: string) => Promise<FxFixingSeries>

interface Entry {
  readonly start: string
  readonly end: string
  readonly fetchedAt: number
  readonly series: Promise<FxFixingSeries>
}

const DAY_MS = 86_400_000

export interface FxSeriesCache {
  /** Rates for `from`→`to` covering trading days `start`..`end` ("YYYY-MM-DD"). Failures are not cached. */
  readonly get: (from: string, to: string, start: string, end: string) => Promise<FxFixingSeries>
  readonly clear: () => void
}

export const createFxSeriesCache = (
  fetch: FetchFxSeries,
  options: { readonly now?: () => number; readonly recentTtlMs?: number; readonly maxPairs?: number } = {}
): FxSeriesCache => {
  const now = options.now ?? Date.now
  const recentTtlMs = options.recentTtlMs ?? 10 * 60_000
  const maxPairs = options.maxPairs ?? 16
  const entries = new Map<string, Entry>()

  /** A range ending more than a week ago can't gain fixings any more. */
  const settled = (end: string) => Date.parse(`${end}T00:00:00Z`) < now() - 8 * DAY_MS

  const get = (from: string, to: string, start: string, end: string): Promise<FxFixingSeries> => {
    const key = `${from}>${to}`

    /** Fetches a range and caches it as the pair's entry; a failure is forgotten. */
    const load = (range: { readonly start: string; readonly end: string }): Promise<FxFixingSeries> => {
      const series = fetch(from, to, range.start, range.end)
      const fresh: Entry = { ...range, fetchedAt: now(), series }
      entries.delete(key)
      entries.set(key, fresh)
      while (entries.size > maxPairs) entries.delete(entries.keys().next().value!)
      series.catch(() => {
        if (entries.get(key) === fresh) entries.delete(key)
      })
      return series
    }

    const entry = entries.get(key)
    if (entry !== undefined && entry.start <= start && entry.end >= end && (settled(end) || now() - entry.fetchedAt < recentTtlMs)) {
      // Most recently used last.
      entries.delete(key)
      entries.set(key, entry)
      // Still in flight for a wider range, it may fail where this one wouldn't: then ask for just this one.
      return entry.series.catch(() => load({ start, end }))
    }
    if (entry === undefined) return load({ start, end })
    // Grow the cached range rather than replace it, so panes on other date ranges keep their hits.
    const wide = { start: start < entry.start ? start : entry.start, end: end > entry.end ? end : entry.end }
    if (wide.start === start && wide.end === end) return load(wide)
    return load(wide).catch(() => load({ start, end }))
  }

  return { get, clear: () => entries.clear() }
}
